import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace("/bim", "") || "/gateWay";

const authApi = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach stored token as Authorization header for non-login requests
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("access-cookie");
  if (token && !config.url?.includes("/auth/login")) {
    config.headers["Authorization"] = token;
  }
  return config;
});

// Handle 401 responses globally — redirect to login.
// Not for the sign-in calls themselves: there a 401 is an answer the form has to show
// ("User Not Found", expired two-step ticket), and a reload would wipe the form and the message.
const PRE_SESSION_CALLS = ["/auth/login", "/auth/mfa/login/", "/users/sendResetEmailToRegisteredUser", "/users/resetPassword"];
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = String(error.config?.url || "");
    if (error.response?.status === 401 && !PRE_SESSION_CALLS.some((p) => url.includes(p))) {
      localStorage.removeItem("access-cookie");
      localStorage.removeItem("currentUser");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export interface LoginRequest {
  email: string;
  password: string;
  applicationType?: string;
  isEmail?: boolean;
}

export interface LoginResponse {
  status: boolean;
  message: string;
  expiresIn: string;
  token: {
    access_token: string;
    refresh_token?: string;
  };
  /** second factor required: no token yet - answer with mfaApi.loginVerify(mfaToken, code) */
  mfaRequired?: boolean;
  /** the user must enrol an authenticator now (enforced) - show the QR first */
  setupRequired?: boolean;
  mfaToken?: string;
}

export interface MfaSetupData { secret: string; otpauthUri: string; qrCodeDataUrl: string; algorithm: string; digits: number; period: number; }
export interface MfaStatus { configured: boolean; enabled: boolean; pending: boolean; confirmedAt: string | null; enforced: boolean; }

const authService = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const payload = {
      email: data.email,
      password: data.password,
      applicationType: data.applicationType || "Buyer",
      isEmail: data.isEmail ?? true,
    };
    const response = await authApi.post<LoginResponse>("/auth/login", payload);

    // Store token for Authorization header on subsequent requests
    if (response.data?.token) {
      localStorage.setItem(
        "access-cookie",
        JSON.stringify(response.data.token)
      );
    }

    return response.data;
  },

  // Request a password-reset email. The gateway derives the reset link from the
  // request Origin and the BIM mail hub (SMTP) delivers the branded email.
  forgotPassword: async (email: string): Promise<any> => {
    const response = await authApi.post("/users/sendResetEmailToRegisteredUser", { email });
    return response.data;
  },

  // New user: "Welcome to ProcureX BIM - set your password" email (same single-use link as a reset,
  // the set-password page then runs the two-step verification set-up).
  inviteUser: async (email: string): Promise<any> => {
    const response = await authApi.post("/users/sendResetEmailToRegisteredUser", { email, invite: true });
    return response.data;
  },

  // Set a new password using the reset token from the emailed link. The token's
  // `sub` claim carries the user id; the gateway's resetPassword updates the password.
  resetPassword: async (token: string, password: string): Promise<any> => {
    let id: number | undefined;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      id = Number(payload.sub ?? payload.id ?? payload.userId);
    } catch { /* invalid token */ }
    if (!id) throw new Error("This reset link is invalid or has expired.");
    const response = await authApi.put(`/users/resetPassword?token=${encodeURIComponent(token)}`, { id, password });
    return response.data;
  },

  logout: async (): Promise<void> => {
    // Best-effort server logout — never let its failure block the client logout.
    try {
      await authApi.post("/auth/logout");
    } catch {
      /* ignore — we still clear local session below */
    }
    localStorage.removeItem("access-cookie");
    localStorage.removeItem("currentUser");
  },

  refresh: async () => {
    const response = await authApi.post("/auth/refresh");
    if (response.data?.token) {
      localStorage.setItem(
        "access-cookie",
        JSON.stringify(response.data.token)
      );
    }
    return response.data;
  },

  isAuthenticated: (): boolean => {
    return !!localStorage.getItem("access-cookie");
  },

  getStoredToken: (): string | null => {
    const cookie = localStorage.getItem("access-cookie");
    if (!cookie) return null;
    try {
      const parsed = JSON.parse(cookie);
      return parsed.access_token || null;
    } catch {
      return null;
    }
  },
};

/** Unwrap a gateway error into a user-facing message. */
export function apiErrorMessage(e: any, fallback = "Something went wrong. Please try again."): string {
  const d = e?.response?.data;
  const m = d?.message || d?.error?.message || d?.error || d?.response?.message || e?.message;
  return (Array.isArray(m) ? m.join(", ") : typeof m === "string" ? m : "") || fallback;
}

// ---- self-service account (the subject is always the signed-in user, from the session) ----
export const accountApi = {
  /** full profile of the signed-in user (DB record: names, phone, role, organisation) */
  getProfile: async (): Promise<any> => {
    const r = await authApi.get("/users/me/profile");
    return r.data?.response ?? r.data?.data ?? r.data;
  },
  updateProfile: async (data: { firstName: string; lastName: string; phoneNumber?: string | null }): Promise<any> => {
    const r = await authApi.put("/users/me/profile", data);
    return r.data?.response ?? r.data;
  },
  /** profile picture: JPEG/PNG/WebP data URL (resized client-side), or null to remove */
  updatePhoto: async (photo: string | null): Promise<any> => {
    const r = await authApi.put("/users/me/photo", { photo });
    return r.data?.response ?? r.data;
  },
  changePassword: async (currentPassword: string, newPassword: string): Promise<any> => {
    const r = await authApi.put("/users/me/password", { currentPassword, newPassword });
    return r.data;
  },
  loginActivity: async (limit = 50): Promise<any[]> => {
    const r = await authApi.get(`/users/me/login-activity?limit=${limit}`);
    const v = r.data?.response ?? r.data?.data ?? r.data;
    return Array.isArray(v) ? v : [];
  },
};

// ---- multi-factor authentication (authenticator app / TOTP) ----
const unwrap = (r: any) => r.data?.response ?? r.data?.data ?? r.data;
export const mfaApi = {
  /** second step of sign-in for a user who must enrol now: QR + manual key (ticket = authentication) */
  loginSetup: async (mfaToken: string, regenerate = false): Promise<MfaSetupData> =>
    unwrap(await authApi.post("/auth/mfa/login/setup", { mfaToken, regenerate })),
  /** second step of sign-in: the 6-digit code; stores the session token like login() */
  loginVerify: async (mfaToken: string, token: string): Promise<LoginResponse & { mfaJustEnabled?: boolean }> => {
    const r = await authApi.post("/auth/mfa/login/verify", { mfaToken, token });
    if (r.data?.token) localStorage.setItem("access-cookie", JSON.stringify(r.data.token));
    return r.data;
  },
  status: async (): Promise<MfaStatus> => unwrap(await authApi.get("/auth/mfa/status")),
  setup: async (regenerate = false): Promise<MfaSetupData> => unwrap(await authApi.post("/auth/mfa/setup", { regenerate })),
  verify: async (token: string): Promise<MfaStatus> => unwrap(await authApi.post("/auth/mfa/verify", { token })),
  disable: async (token: string): Promise<MfaStatus> => unwrap(await authApi.post("/auth/mfa/disable", { token })),
  adminReset: async (userId: number): Promise<any> => unwrap(await authApi.delete(`/auth/mfa/admin/${userId}`)),
};

export { authApi };
export const logout = authService.logout;
export default authService;
